import { useState, type FormEvent } from "react";
import { motion } from "framer-motion";
import { ArrowRight, CircleAlert, KeyRound, RefreshCw, ShieldCheck } from "lucide-react";
import type { SessionResponse } from "../shared/contracts";
import { api, ApiError } from "./api";

const MINIMUM = 8;

/** Intercepta o primeiro acesso: enquanto a senha inicial não muda, nada mais da API responde. */
export function ChangePasswordFirstLogin({ session, onChanged, onLogout }: {
  session: SessionResponse; onChanged: (session: SessionResponse) => void; onLogout: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (password !== confirmation) { setError("As duas senhas precisam ser iguais."); return; }
    if (password.length < MINIMUM) { setError(`Use ao menos ${MINIMUM} caracteres.`); return; }
    setBusy(true); setError("");
    try { onChanged(await api.changePassword(password)); }
    catch (reason) {
      setError(reason instanceof ApiError ? reason.message : "Não foi possível alterar a senha.");
    } finally { setBusy(false); }
  }

  async function logout() { await api.logout().catch(() => undefined); onLogout(); }

  return (
    <main className="login-shell">
      <motion.section className="login-brand" initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: .5 }}>
        <div className="login-brand-top"><div className="brand-mark light">TX</div><span>TAXOS</span></div>
        <div className="login-copy">
          <p className="eyebrow">PRIMEIRO ACESSO</p>
          <h1>Defina sua senha<br />para continuar.</h1>
          <p>A senha que você recebeu do administrador é temporária e vale apenas para esta primeira entrada.</p>
        </div>
        <div className="login-proof"><ShieldCheck size={18} /><span>Só você conhece a nova senha · guardada com hash no servidor</span></div>
      </motion.section>
      <motion.section className="login-form-wrap" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: .12 }}>
        <form className="login-form" onSubmit={submit}>
          <p className="eyebrow ink">ALTERE SUA SENHA</p>
          <h2>Bem-vindo, {session.user.name.split(" ")[0]}</h2>
          <p className="first-login-note">Escolha uma senha com ao menos {MINIMUM} caracteres. Esta tela não volta a aparecer.</p>
          <label>Nova senha
            <input type="password" autoComplete="new-password" autoFocus value={password} onChange={e => setPassword(e.target.value)} />
          </label>
          <label>Repita a nova senha
            <input type="password" autoComplete="new-password" value={confirmation} onChange={e => setConfirmation(e.target.value)} />
          </label>
          {error && <div className="inline-error"><CircleAlert size={16} />{error}</div>}
          <button className="primary-button wide" disabled={busy}>
            {busy ? <RefreshCw className="spin" size={17} /> : <><KeyRound size={16} /> Salvar e entrar <ArrowRight size={17} /></>}
          </button>
          <button type="button" className="text-button first-login-exit" onClick={logout}>Sair e entrar com outra conta</button>
        </form>
      </motion.section>
    </main>
  );
}
